import {
  Popover,
  List,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Radio,
  FormControlLabel,
  Typography,
  Box,
  ListItem,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useState, useRef, KeyboardEvent } from 'react';

const KnownIssuesFilterDropdown = ({
  selectedFilters,
  setSelectedFilters,
  affectedApis,
  status,
  environments,
  staticContent
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [tempFilters, setTempFilters] = useState(selectedFilters);
  // const [focusedAccordion, setFocusedAccordion] = useState<string | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Add these refs for navigation
  const accordionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const firstOptionRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const optionRefs = useRef<Record<string, (HTMLElement | null)[]>>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const open = Boolean(anchorEl);

  // Update temp filters when selectedFilters change
  useEffect(() => {
    setTempFilters(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    const initialExpanded: Record<string, boolean> = {};
    Object.keys(filterOptions).forEach((category) => {
      initialExpanded[category] = true;
    });
    setExpanded(initialExpanded);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      // Store current scroll position
      const scrollY = window.scrollY;

      // Apply styles to prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore scroll position and remove fixed positioning
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  const filterOptions = {
    affectedApis: Array.isArray(affectedApis)
      ? [
          ...(affectedApis[0] !== staticContent.showAll
            ? [staticContent.showAll]
            : []),
          ...affectedApis.filter((a) => a !== staticContent.showAll)
        ]
      : [staticContent.showAll],
    status: Array.isArray(status)
      ? [
          ...(status[0] !== staticContent.showAll
            ? [staticContent.showAll]
            : []),
          ...status.filter((s) => s !== staticContent.showAll)
        ]
      : [staticContent.showAll],
    environments: Array.isArray(environments)
      ? [
          ...(environments[0] !== staticContent.showAll
            ? [staticContent.showAll]
            : []),
          ...environments.filter((e) => e !== staticContent.showAll)
        ]
      : [staticContent.showAll]
  };

  // Add safety checks before modifying arrays
  if (
    filterOptions.status.length > 0 &&
    filterOptions.status[0] !== staticContent.showAll
  ) {
    filterOptions.status.unshift(staticContent.showAll);
  } else if (filterOptions.status.length === 0) {
    filterOptions.status = [staticContent.showAll];
  }

  if (
    filterOptions.environments.length > 0 &&
    filterOptions.environments[0] !== staticContent.showAll
  ) {
    filterOptions.environments.unshift(staticContent.showAll);
  } else if (filterOptions.environments.length === 0) {
    filterOptions.environments = [staticContent.showAll];
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'affectedApis':
        return staticContent?.filterDrop?.[0] || 'Filter by Affected API(s)';
      case 'status':
        return staticContent?.filterDrop?.[1] || 'Filter by Status';
      case 'environments':
        return staticContent?.filterDrop?.[2] || 'Filter by environment';
      default:
        return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const handleExpand =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded((prev) => ({
        ...prev,
        [panel]: isExpanded
      }));
    };

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setTempFilters(selectedFilters);

    // Set focus to first accordion after opening
    setTimeout(() => {
      const firstCategory = Object.keys(filterOptions)[0];
      // setFocusedAccordion(firstCategory);
      accordionRefs.current[firstCategory]?.focus();
    }, 100);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        setAnchorEl(event.currentTarget);
        setTempFilters(selectedFilters);

        // Set focus to first accordion after opening
        setTimeout(() => {
          const firstCategory = Object.keys(filterOptions)[0];
          // setFocusedAccordion(firstCategory);
          accordionRefs.current[firstCategory]?.focus();
        }, 100);
      } else {
        handleClose();
      }
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setTempFilters(selectedFilters);
      handleClose();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setExpanded({});
    // setFocusedAccordion(null);
    setTempFilters(selectedFilters);
    triggerRef.current?.focus();
  };

  const handleSelect = (category, option) => {
    if (option === staticContent.showAll) {
      setTempFilters((prev) => ({
        ...prev,
        [category]: [staticContent.showAll]
      }));
      // Immediately apply on desktop/tablet
      if (!isMobile) {
        setSelectedFilters((prev) => ({
          ...prev,
          [category]: [staticContent.showAll]
        }));
      }
    } else {
      setTempFilters((prev) => {
        const current = prev[category] || [];
        let updated;
        if (current.includes(option)) {
          updated = current.filter(
            (item) => item !== option && item !== staticContent.showAll
          );
          if (updated.length === 0) {
            updated = [staticContent.showAll];
          }
        } else {
          updated = [
            ...current.filter((item) => item !== staticContent.showAll),
            option
          ];
        }
        // Immediately apply on desktop/tablet
        if (!isMobile) {
          setSelectedFilters((prev) => ({
            ...prev,
            [category]: updated
          }));
        }
        return { ...prev, [category]: updated };
      });
    }
  };

  const handleApplyFilters = () => {
    // Only apply on mobile
    if (isMobile) {
      setSelectedFilters(tempFilters);
      handleClose();
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleAccordionKeyDown = (event: KeyboardEvent, panel: string) => {
    const categories = Object.keys(filterOptions);
    const currentIndex = categories.indexOf(panel);

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      const wasExpanded = expanded[panel];
      setExpanded((prev) => ({
        ...prev,
        [panel]: !prev[panel]
      }));

      // If expanding, focus on first option
      if (!wasExpanded) {
        setTimeout(() => {
          const firstOptionRef = firstOptionRefs.current[panel];
          if (firstOptionRef) {
            firstOptionRef.focus();
          }
        }, 100);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (expanded[panel]) {
        // If accordion is expanded, focus on first option
        const firstOptionRef = firstOptionRefs.current[panel];
        if (firstOptionRef) {
          firstOptionRef.focus();
        }
      } else {
        // Move to next accordion
        const nextIndex =
          currentIndex < categories.length - 1 ? currentIndex + 1 : 0;
        const nextCategory = categories[nextIndex];
        // setFocusedAccordion(nextCategory);
        const nextAccordionRef = accordionRefs.current[nextCategory];
        if (nextAccordionRef) {
          nextAccordionRef.focus();
        }
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      const prevIndex =
        currentIndex > 0 ? currentIndex - 1 : categories.length - 1;
      const prevCategory = categories[prevIndex];
      // setFocusedAccordion(prevCategory);
      const prevAccordionRef = accordionRefs.current[prevCategory];
      if (prevAccordionRef) {
        prevAccordionRef.focus();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setTempFilters(selectedFilters);
      setExpanded({});
      handleClose();
    }
  };

  const handleOptionKeyDown = (
    event: KeyboardEvent,
    category: string,
    option: string,
    optionIndex: number
  ) => {
    const categories = Object.keys(filterOptions);
    const currentCategoryIndex = categories.indexOf(category);
    const categoryOptions = filterOptions[category];
    const currentOptionIndex = optionIndex;

    if (event.key === ' ') {
      event.preventDefault();
      handleSelect(category, option);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      handleSelect(category, option);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();

      // Move to next option in the same category
      if (currentOptionIndex < categoryOptions.length - 1) {
        const nextOptionIndex = currentOptionIndex + 1;
        const nextOptionRef = optionRefs.current[category]?.[nextOptionIndex];
        if (nextOptionRef) {
          // Find the actual input element within the MUI component
          const inputElement =
            nextOptionRef.querySelector('input') || nextOptionRef;
          setTimeout(() => {
            inputElement.focus();
          }, 0);
        }
      } else {
        // If at last option, move to next accordion
        const nextCategoryIndex =
          currentCategoryIndex < categories.length - 1
            ? currentCategoryIndex + 1
            : 0;
        const nextCategory = categories[nextCategoryIndex];
        // setFocusedAccordion(nextCategory);
        const nextAccordionRef = accordionRefs.current[nextCategory];
        if (nextAccordionRef) {
          setTimeout(() => {
            nextAccordionRef.focus();
          }, 0);
        }
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();

      // Move to previous option in the same category
      if (currentOptionIndex > 0) {
        const prevOptionIndex = currentOptionIndex - 1;
        const prevOptionRef = optionRefs.current[category]?.[prevOptionIndex];
        if (prevOptionRef) {
          // Find the actual input element within the MUI component
          const inputElement =
            prevOptionRef.querySelector('input') || prevOptionRef;
          setTimeout(() => {
            inputElement.focus();
          }, 0);
        }
      } else {
        // If at first option, go back to accordion header
        const accordionRef = accordionRefs.current[category];
        if (accordionRef) {
          setTimeout(() => {
            accordionRef.focus();
          }, 0);
        }
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setTempFilters(selectedFilters);
      setExpanded({});
      handleClose();
      triggerRef.current?.focus();
    }
  };

  const handleApplyKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleApplyFilters();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setTempFilters(selectedFilters); // Reset filters
      handleClose();
    }
  };

  const handleCancelKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCancel();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  return (
    <>
      <Box
        ref={triggerRef}
        sx={{
          minWidth: '272px',
          mt: '5px',
          width: { xs: '100%', sm: '272px' },
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'transparent',
          borderBottom: '1px solid #ccc',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:focus': {
            outline: '2px solid #1460AA',
            outlineOffset: '2px',
            backgroundColor: 'rgba(20, 96, 170, 0.04)'
          }
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label="Open filter options"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ color: '#1460AA',  width: '20px', height: '20px',  }} />
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '20px',
              fontWeight: 600,
              color: '#000000',
              mb: '5px'
            }}
          >
            {staticContent.filterLabel}
          </Typography>
        </Box>
        {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock={true}
        disablePortal={true}
        disableRestoreFocus={true}
        transitionDuration={0}
      >
        {isMobile ? (
          <Box
            sx={{
              width: '90vw',
              maxWidth: '100vw',
              background: 'white',
              minHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                background: 'white',
                position: 'sticky',
                top: 0,
                zIndex: 2
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                {staticContent.filterLabel}
              </Typography>
              <ClearIcon
                sx={{ cursor: 'pointer' }}
                aria-label="Close filter"
                tabIndex={0}
                onClick={handleClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClose();
                  }
                }}
                role="button"
              />
            </Box>
            {/* Accordions */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: '10px',
                borderRadius: '16px',
                // pb: 10/
              }}
            >
              {Object.entries(filterOptions).map(
                ([category, options], index) => {
                  const sectionLabel = getCategoryLabel(category);
                  return (
                    <Accordion
                      key={category}
                      expanded={expanded[category] || false}
                      onChange={handleExpand(category)}
                      sx={{
                        background: '#F1F7FB',
                        '&.Mui-expanded': { m: 0 }
                      }}
                      aria-expanded={expanded[category] || false}
                      aria-controls={`section-${category}`}
                    >
                      <AccordionSummary
                        ref={(el) => (accordionRefs.current[category] = el)}
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          m: 0,
                          '&.Mui-focusVisible': {
                            outline: '2px solid #1460AA',
                            outlineOffset: '2px'
                          }
                        }}
                        onKeyDown={(event) => {
                          if (
                            event.key === 'ArrowUp' ||
                            event.key === 'ArrowDown' ||
                            event.key === 'Enter' ||
                            event.key === ' '
                          ) {
                            event.preventDefault();
                            event.stopPropagation();
                            event.nativeEvent.stopImmediatePropagation();
                          }
                          handleAccordionKeyDown(event, category);
                        }}
                        tabIndex={0}
                        aria-label={`${sectionLabel} filter section`}
                        aria-expanded={expanded[category] || false}
                        aria-controls={`section-${category}`}
                        id={`accordion-header-${category}`}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {sectionLabel}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          background: 'white',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          overflowX: 'hidden'
                        }}
                      >
                        <section
                          id={`section-${category}`}
                          aria-label={`Filter by ${sectionLabel}`}
                        >
                          <List role="group" aria-label={`${category} options`}>
                            {Array.isArray(options) &&
                              options.map((option, idx) => {
                                const ariaLabel = `${sectionLabel}: ${option}`;
                                const isSelected = (
                                  tempFilters[category] || []
                                ).includes(option);
                                return (
                                  <ListItem
                                    key={option}
                                    sx={{
                                      '&:focus-within': {
                                        backgroundColor:
                                          'rgba(20, 96, 170, 0.04)'
                                      }
                                    }}
                                    role="option"
                                    aria-selected={isSelected}
                                  >
                                    {idx === 0 ? (
                                      <FormControlLabel
                                        control={
                                          <Radio
                                            ref={(el) => {
                                              if (idx === 0)
                                                firstOptionRefs.current[
                                                  category
                                                ] =
                                                  el as HTMLInputElement | null;
                                              if (
                                                !optionRefs.current[category]
                                              ) {
                                                optionRefs.current[category] =
                                                  [];
                                              }
                                              if (el)
                                                optionRefs.current[category][
                                                  idx
                                                ] = el;
                                            }}
                                            checked={(
                                              tempFilters[category] || []
                                            ).includes(staticContent.showAll)}
                                            onChange={() =>
                                              handleSelect(
                                                category,
                                                staticContent.showAll
                                              )
                                            }
                                            onKeyDown={(event) =>
                                              handleOptionKeyDown(
                                                event,
                                                category,
                                                staticContent.showAll,
                                                idx
                                              )
                                            }
                                            sx={{
                                              '&.Mui-focusVisible': {
                                                outline: '2px solid #1460AA',
                                                outlineOffset: '2px'
                                              }
                                            }}
                                            aria-label={ariaLabel}
                                            role="option"
                                            aria-selected={(
                                              tempFilters[category] || []
                                            ).includes(staticContent.showAll)}
                                          />
                                        }
                                        label={
                                          <span aria-hidden="true">
                                            {option}
                                          </span>
                                        }
                                        sx={{
                                          '& .MuiFormControlLabel-label': {
                                            fontSize: '14px'
                                          }
                                        }}
                                      />
                                    ) : (
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            ref={(el) => {
                                              if (
                                                !optionRefs.current[category]
                                              ) {
                                                optionRefs.current[category] =
                                                  [];
                                              }
                                              if (el)
                                                optionRefs.current[category][
                                                  idx
                                                ] = el;
                                            }}
                                            checked={isSelected}
                                            onChange={() => {
                                              handleSelect(category, option);
                                            }}
                                            onKeyDown={(event) =>
                                              handleOptionKeyDown(
                                                event,
                                                category,
                                                option,
                                                idx
                                              )
                                            }
                                            sx={{
                                              '&.Mui-focusVisible': {
                                                outline: '2px solid #1460AA',
                                                outlineOffset: '2px'
                                              }
                                            }}
                                            aria-label={ariaLabel}
                                            role="option"
                                            aria-selected={isSelected}
                                          />
                                        }
                                        label={
                                          <span aria-hidden="true">
                                            {option}
                                          </span>
                                        }
                                        sx={{
                                          '& .MuiFormControlLabel-label': {
                                            fontSize: '14px'
                                          }
                                        }}
                                      />
                                    )}
                                  </ListItem>
                                );
                              })}
                          </List>
                        </section>
                      </AccordionDetails>
                    </Accordion>
                  );
                }
              )}
            </Box>
            {/* Mobile buttons inside the white box */}
            <Box
              sx={{
                p: 2,
                backgroundColor: 'white',
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                zIndex: theme.zIndex.modal + 1,
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
              }}
            >
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                onKeyDown={handleApplyKeyDown}
                sx={{
                  backgroundColor: '#1460AA',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '24px',
                  '&:hover': {
                    backgroundColor: '#0f4c8c'
                  },
                  '&:focus': {
                    outline: '2px solid #ffffff',
                    outlineOffset: '2px'
                  }
                }}
                aria-label="Apply filter"
              >
                Apply Filter
              </Button>
              <Button
                onClick={handleCancel}
                onKeyDown={handleCancelKeyDown}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '4px',
                  '&:focus': {
                    outline: '2px solid #1460AA',
                    outlineOffset: '2px'
                  }
                }}
                aria-label="Cancel filter"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          // Desktop/tablet view (existing)
          <Box
            sx={{
              width: { xs: '100vw', sm: '272px' },
              maxWidth: { xs: '100vw', sm: '272px' }
            }}
          >
            {Object.entries(filterOptions).map(([category, options], index) => {
              const sectionLabel = getCategoryLabel(category);
              return (
                <Accordion
                  key={category}
                  expanded={expanded[category] || false}
                  onChange={handleExpand(category)}
                  sx={{
                    background: '#F1F7FB',
                    '&.Mui-expanded': {
                      m: 0
                    }
                  }}
                  aria-expanded={expanded[category] || false}
                  aria-controls={`section-${category}`}
                >
                  <AccordionSummary
                    ref={(el) => (accordionRefs.current[category] = el)}
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      m: 0,
                      '&.Mui-focusVisible': {
                        outline: '2px solid #1460AA',
                        outlineOffset: '2px'
                      }
                    }}
                    onKeyDown={(event) => {
                      if (
                        event.key === 'ArrowUp' ||
                        event.key === 'ArrowDown' ||
                        event.key === 'Enter' ||
                        event.key === ' '
                      ) {
                        event.preventDefault();
                        event.stopPropagation();
                        event.nativeEvent.stopImmediatePropagation();
                      }
                      handleAccordionKeyDown(event, category);
                    }}
                    tabIndex={0}
                    aria-label={`${sectionLabel} filter section`}
                    aria-expanded={expanded[category] || false}
                    aria-controls={`section-${category}`}
                    id={`accordion-header-${category}`}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {sectionLabel}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      background: 'white',
                      height: '200px',
                      overflowY: 'auto',
                      overflowX: 'hidden'
                    }}
                  >
                    <section
                      id={`section-${category}`}
                      aria-label={`Filter by ${sectionLabel}`}
                    >
                      <List role="group" aria-label={`${category} options`}>
                        {Array.isArray(options) &&
                          options.map((option, idx) => {
                            const ariaLabel = `${sectionLabel}: ${option}`;
                            const isSelected = (
                              tempFilters[category] || []
                            ).includes(option);
                            return (
                              <ListItem
                                key={option}
                                sx={{
                                  '&:focus-within': {
                                    backgroundColor: 'rgba(20, 96, 170, 0.04)'
                                  }
                                }}
                                role="option"
                                aria-selected={isSelected}
                              >
                                {idx === 0 ? (
                                  <FormControlLabel
                                    control={
                                      <Radio
                                        ref={(el) => {
                                          if (idx === 0)
                                            firstOptionRefs.current[category] =
                                              el as HTMLInputElement | null;
                                          if (!optionRefs.current[category]) {
                                            optionRefs.current[category] = [];
                                          }
                                          if (el)
                                            optionRefs.current[category][idx] =
                                              el;
                                        }}
                                        checked={(
                                          tempFilters[category] || []
                                        ).includes(staticContent.showAll)}
                                        onChange={() =>
                                          handleSelect(
                                            category,
                                            staticContent.showAll
                                          )
                                        }
                                        onKeyDown={(event) =>
                                          handleOptionKeyDown(
                                            event,
                                            category,
                                            staticContent.showAll,
                                            idx
                                          )
                                        }
                                        sx={{
                                          '&.Mui-focusVisible': {
                                            outline: '2px solid #1460AA',
                                            outlineOffset: '2px'
                                          }
                                        }}
                                        aria-label={ariaLabel}
                                        role="option"
                                        aria-selected={(
                                          tempFilters[category] || []
                                        ).includes(staticContent.showAll)}
                                      />
                                    }
                                    label={
                                      <span aria-hidden="true">{option}</span>
                                    }
                                    sx={{
                                      '& .MuiFormControlLabel-label': {
                                        fontSize: '14px'
                                      }
                                    }}
                                  />
                                ) : (
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        ref={(el) => {
                                          if (!optionRefs.current[category]) {
                                            optionRefs.current[category] = [];
                                          }
                                          if (el)
                                            optionRefs.current[category][idx] =
                                              el;
                                        }}
                                        checked={isSelected}
                                        onChange={() => {
                                          handleSelect(category, option);
                                        }}
                                        onKeyDown={(event) =>
                                          handleOptionKeyDown(
                                            event,
                                            category,
                                            option,
                                            idx
                                          )
                                        }
                                        sx={{
                                          '&.Mui-focusVisible': {
                                            outline: '2px solid #1460AA',
                                            outlineOffset: '2px'
                                          }
                                        }}
                                        aria-label={ariaLabel}
                                        role="option"
                                        aria-selected={isSelected}
                                      />
                                    }
                                    label={
                                      <span aria-hidden="true">{option}</span>
                                    }
                                    sx={{
                                      '& .MuiFormControlLabel-label': {
                                        fontSize: '14px'
                                      }
                                    }}
                                  />
                                )}
                              </ListItem>
                            );
                          })}
                      </List>
                    </section>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </Popover>
    </>
  );
};

export default KnownIssuesFilterDropdown;
